import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  title: string;
  type: string;
  desc: string;
  image: string;
  href: string;
}

const SLIDES: Slide[] = [
  {
    id: "w11",
    title: "锦岁长安",
    type: "古装 · 权谋 · 全 40 集",
    desc: "朝堂之上人人落子，只有她敢把棋盘掀了。三条叙事线如何拧成一股绳，本周深度复盘。",
    image: "https://readdy.ai/api/search-image?query=cinematic%20wide%20artistic%20poster%20of%20an%20ancient%20chinese%20palace%20complex%20at%20dusk%20with%20warm%20lantern%20glow%20and%20drifting%20mist%2C%20deep%20indigo%20sky%20with%20amber%20lights%2C%20epic%20moody%20film%20aesthetic%20with%20subtle%20grain%2C%20no%20people%20no%20text&width=1600&height=760&seq=hero-slide-01&orientation=landscape",
    href: "/detail/w11"
  },
  {
    id: "w03",
    title: "逆流黎明",
    type: "科幻 · 剧情 · 141 分钟",
    desc: "当时间开始倒流，所有人都在奔向同一个黎明。时间线全梳理，结局并非宿命。",
    image: "https://readdy.ai/api/search-image?query=cinematic%20wide%20artistic%20poster%20of%20a%20futuristic%20city%20skyline%20at%20dawn%20with%20towering%20glass%20structures%20and%20soft%20indigo%20mist%2C%20distant%20warm%20amber%20lights%2C%20epic%20faithful%20film%20aesthetic%20with%20subtle%20grain%2C%20no%20people%20no%20text&width=1600&height=760&seq=hero-slide-02&orientation=landscape",
    href: "/detail/w03"
  },
  {
    id: "w13",
    title: "深海的信",
    type: "悬疑 · 剧情 · 全 24 集",
    desc: "三封漂流瓶里的信，串起一个从未寄出的真相。有些话只能隔着海去说。",
    image: "https://readdy.ai/api/search-image?query=cinematic%20wide%20artistic%20poster%20of%20a%20dark%20ocean%20surface%20with%20moonlight%20ripples%20and%20a%20distant%20lighthouse%2C%20deep%20indigo%20tones%20with%20soft%20warm%20highlight%2C%20mysterious%20film%20aesthetic%20with%20subtle%20grain%2C%20no%20people%20no%20text&width=1600&height=760&seq=hero-slide-03&orientation=landscape",
    href: "/detail/w13"
  },
  {
    id: "w29",
    title: "悬案笔记",
    type: "悬疑 · 犯罪 · 全 24 集",
    desc: "一本写满案情的旧笔记，每一页都比凶手早一步。为什么观众愿意逐帧分析？",
    image: "https://readdy.ai/api/search-image?query=cinematic%20wide%20artistic%20poster%20of%20a%20rainy%20night%20city%20alley%20with%20neon%20reflections%20on%20wet%20ground%2C%20dark%20indigo%20tones%20with%20amber%20accents%2C%20moody%20mystery%20film%20aesthetic%20with%20subtle%20grain%2C%20no%20people%20no%20text&width=1600&height=760&seq=hero-slide-04&orientation=landscape",
    href: "/detail/w29"
  }
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const go = (index: number) => {
    setCurrent((index + SLIDES.length) % SLIDES.length);
  };

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  const onTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };
  const onTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? current + 1 : current - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section className="relative w-full">
      <div
        className="relative h-[440px] w-full overflow-hidden bg-primary-950 md:h-[560px]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {SLIDES.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 焦点剧照`}
              title={`${slide.title} 影视剧资讯与剧情解析`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 via-background-950/55 to-background-950/20"></div>
            <div className="absolute inset-0 mx-auto flex w-full max-w-6xl flex-col justify-center px-4 md:px-6 lg:px-8">
              <div className="w-full max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/95 px-3.5 py-1.5 text-xs font-medium text-background-50">
                  <i className="ri-fire-line"></i>
                  焦点推荐
                </span>
                <h2 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-5xl">{slide.title}</h2>
                <p className="mt-2 text-sm text-primary-100 md:text-base">{slide.type}</p>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">{slide.desc}</p>
                <Link
                  to={slide.href}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看剧情与角色分析
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => go(current - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(current + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {SLIDES.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(index)}
              aria-label={`切换到第 ${index + 1} 张`}
              className={`h-1.5 rounded-full transition-all ${
                index === current ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}